import { supabase } from '../clients/supabaseClient';
import type { Customer, Address } from '../types';

const CUSTOMER_SELECT = `
    id,
    first_name,
    last_name,
    cellphone,
    delivery_address,
    created_at,
    primary_address:addresses!customers_delivery_address_fkey (
        id, address_1, address_2, suburb, city, postcode
    )
`;

export async function fetchCustomersFromDb(): Promise<Customer[]> {
    const { data, error } = await supabase
        .from('customers')
        .select(CUSTOMER_SELECT)
        .is('deleted_at', null)
        .order('first_name', { ascending: true });

    if (error) throw error;
    return (data ?? []).map((row) => ({
        ...row,
        primary_address: Array.isArray(row.primary_address)
            ? row.primary_address[0] ?? null
            : row.primary_address ?? null,
    })) as Customer[];

}

export type CreateCustomerPayload = {
    first_name: string;
    last_name: string;
    cellphone?: string | null;
    delivery_address?: string | null;
};

export async function createCustomerInDb(payload: CreateCustomerPayload): Promise<Customer> {
    const { data, error } = await supabase
        .from('customers')
        .insert([payload])
        .select(CUSTOMER_SELECT)
        .single();

    if (error) throw error;
    return {
        ...data,
        primary_address: Array.isArray((data).primary_address)
            ? (data).primary_address[0] ?? null
            : (data).primary_address ?? null,
    } as Customer;
}

export type UpdateCustomerPayload = Partial<CreateCustomerPayload>;

export async function updateCustomerInDb(id: string, patch: UpdateCustomerPayload): Promise<Customer> {
    const { data, error } = await supabase
        .from('customers')
        .update(patch)
        .eq('id', id)
        .select(CUSTOMER_SELECT);

    if (error) throw error;
    if (!data || data.length === 0) throw new Error('Customer not found');
    return {
        ...data[0],
        primary_address: Array.isArray(data[0].primary_address)
            ? data[0].primary_address[0] ?? null
            : data[0].primary_address ?? null,
    } as Customer;
}

export async function fetchAddressesForCustomerFromDb(customerId: string): Promise<Address[]> {
    const { data, error } = await supabase
        .from('addresses')
        .select('*')
        .eq('customer_id', customerId);

    if (error) throw error;
    return (data ?? []) as Address[];
}

export type CreateAddressPayload = {
    customer_id: string;
    address_1: string;
    address_2?: string | null;
    suburb?: string | null;
    city?: string | null;
    postcode?: string | null;
    latitude?: string | null;
    longitude?: string | null;
};

export async function createAddressInDb(payload: CreateAddressPayload): Promise<Address> {
    const { data, error } = await supabase
        .from('addresses')
        .insert([payload])
        .select()
        .single();

    if (error) throw error;
    return data as Address;
}

export async function setCustomerPrimaryAddressInDb(
    customerId: string,
    addressId: string
): Promise<void> {
    const { error } = await supabase
        .from('customers')
        .update({ delivery_address: addressId })
        .eq('id', customerId);

    if (error) throw error;
}

export type CustomerOrder = {
    id: string;
    reference: string;
    status: string;
    created_at: string;
    delivered_at: string | null;
};

export async function fetchOrdersForCustomerFromDb(customerId: string): Promise<CustomerOrder[]> {
    const { data, error } = await supabase
        .from('orders')
        .select(`
            id,
            reference,
            status,
            created_at,
            deliveries ( id, status, delivered_at )
        `)
        .eq('customer_id', customerId)
        .order('created_at', { ascending: false });

    if (error) throw error;

    return (data ?? []).map((o) => {
        const deliveries = Array.isArray(o.deliveries) ? o.deliveries : (o.deliveries ? [o.deliveries] : []);
        const completed = deliveries.find((d) =>
            ['DELIVERED', 'DELIVERY_COMPLETE'].includes(d.status?.toUpperCase())
        );
        return {
            id: o.id,
            reference: o.reference,
            status: o.status,
            created_at: o.created_at,
            delivered_at: completed?.delivered_at ?? null,
        };
    });
}

export async function fetchAddressByIdFromDb(addressId: string): Promise<Address | null> {
    const { data, error } = await supabase
        .from('addresses')
        .select('*')
        .eq('id', addressId)
        .single();

    if (error) throw error;
    return data as Address;
}